import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';
import type { AudienceType } from '@/components/home/PersonaSelector';

const STORAGE_KEY = 'youtemp_persona';
const VALID: AudienceType[] = ['agencies', 'employers', 'temps'];

interface PersonaContextType {
  activeAudience: AudienceType | null;
  setActiveAudience: (audience: AudienceType | null) => void;
  isPersonaSelectorEnabled: boolean;
}

const PersonaContext = createContext<PersonaContextType | undefined>(undefined);

// Static value used when persona selector is disabled — avoids allocations
const DISABLED_VALUE: PersonaContextType = {
  activeAudience: null,
  setActiveAudience: () => {},
  isPersonaSelectorEnabled: false,
};

function readStoredPersona(): AudienceType | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored && VALID.includes(stored as AudienceType)
      ? (stored as AudienceType)
      : null;
  } catch {
    return null;
  }
}

export function PersonaProvider({ children }: { children: ReactNode }) {
  const { featureFlags, isPublicFlagsLoading } = usePlatformSettings();
  const enabled = featureFlags.enable_header_persona_selector;
  // CRITICAL: Initial render MUST be `null` so the first client commit matches
  // the prerendered HTML (the SSR snapshot has no access to sessionStorage and
  // always emits the default persona view). Reading the stored persona in the
  // initial `useState` initialiser would swap the hero H1 highlight class
  // during hydration, repainting the LCP element and pushing LCP render delay
  // out by ~1.4 s. Restore the stored persona in an effect after hydration.
  const [audience, setAudience] = useState<AudienceType | null>(null);

  useEffect(() => {
    // Defer the stored-persona read until after LCP fires. Restoring the
    // persona synchronously on mount swaps the Hero H1 highlight class and
    // recolours the Header logo during the hydration window, which the
    // browser records as the final LCP frame and adds ~1s of element
    // render delay. A PerformanceObserver lets us wait for the actual LCP
    // event; a 2s safety timeout covers browsers without LCP support.
    let done = false;
    const apply = () => {
      if (done) return;
      done = true;
      const stored = readStoredPersona();
      if (stored) setAudience(stored);
    };
    let po: PerformanceObserver | null = null;
    try {
      po = new PerformanceObserver(() => {
        // Wait one frame after LCP so the paint commits before the swap.
        requestAnimationFrame(apply);
        po?.disconnect();
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true } as PerformanceObserverInit);
    } catch {
      // PerformanceObserver / entry type unsupported — fall back to timeout.
    }
    const t = window.setTimeout(apply, 2000);
    return () => {
      window.clearTimeout(t);
      po?.disconnect();
    };
  }, []);

  const setActiveAudience = useCallback((next: AudienceType | null) => {
    if (!enabled) return;
    setAudience(next);
    try {
      if (next) {
        sessionStorage.setItem(STORAGE_KEY, next);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* SSR / private browsing guard */ }
  }, [enabled]);

  // While flags are loading, return stored audience optimistically to prevent flash.
  // Once loaded: if enabled, use live state; if disabled, return DISABLED_VALUE.
  let value: PersonaContextType;
  if (isPublicFlagsLoading) {
    value = { activeAudience: audience, setActiveAudience, isPersonaSelectorEnabled: false };
  } else if (enabled) {
    value = { activeAudience: audience, setActiveAudience, isPersonaSelectorEnabled: true };
  } else {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* guard */ }
    value = DISABLED_VALUE;
  }

  return (
    <PersonaContext.Provider value={value}>
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona(): PersonaContextType {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within PersonaProvider');
  }
  return context;
}
